import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';

const questions = [
  {
    text: "What best describes you?",
    options: [
      "I'm a business owner",
      "I'm self-employed / freelancer",
      "I'm an individual or family (W-2 employee)",
      "I'm a real estate investor",
      "I'm retired or approaching retirement"
    ]
  },
  {
    text: "What's your approximate annual income?",
    options: [
      "Under $75K",
      "$75K - $150K",
      "$150K - $300K",
      "$300K - $500K",
      "Over $500K"
    ]
  },
  {
    text: "How would you describe your current tax situation?",
    options: [
      "I have a CPA but I'm not sure I'm getting the best advice",
      "I do my own taxes or use software (TurboTax, etc.)",
      "I have an unresolved IRS issue (back taxes, audit, lien, etc.)",
      "I recently had a major life change (marriage, divorce, home purchase, new child)",
      "I'm starting or restructuring a business and need guidance"
    ]
  },
  {
    text: "What matters most to you right now?",
    options: [
      "Paying less in taxes this year",
      "Understanding my best filing status (single, joint, separate, head of household)",
      "Getting my books and finances organized",
      "Resolving a tax problem or IRS issue",
      "Having a trusted advisor I can call anytime"
    ]
  }
];

const getResultText = (answers) => {
  const [type, revenue, situation, priority] = answers;

  // IRS issues — universal
  if (situation === 2) {
    return "IRS issues rarely resolve themselves — and the longer they sit, the more expensive they get. KGOB has a dedicated tax resolution team that has successfully negotiated liens, levies, and offers in compromise for hundreds of clients. The first step is understanding exactly what the IRS has on file. We can help with that.";
  }

  // Life change — individual focus
  if (situation === 3) {
    return "Major life events — marriage, divorce, a new child, buying a home — can significantly change your tax picture. Filing status alone (single, married filing jointly, married filing separately, head of household) can mean thousands of dollars in difference. Many people don't realize they may qualify for new credits, deductions, or a more favorable filing status after a life change. We help individuals navigate these transitions so you never pay more than you need to.";
  }

  // Filing status priority — individual focus
  if (priority === 1) {
    return "Choosing the right filing status is one of the most impactful tax decisions an individual can make — and it's not always obvious. Married filing jointly vs. separately, head of household qualifications, and dependent credits can each shift your tax bill by thousands. We see individuals every year who've been filing under the wrong status without realizing it. A quick consultation can clarify exactly where you stand and whether a change could save you money.";
  }

  // Business owner with higher income
  if (type === 0 && revenue >= 2 && situation === 0) {
    return "Based on businesses similar to yours, there's a strong chance you could benefit from a tax strategy review. Business owners in the $150K+ range frequently uncover $15,000-$40,000+ in annual savings through entity restructuring, retirement vehicle optimization, and proactive deduction planning. Most CPAs don't do this work — we do.";
  }

  // Individual using DIY tax software
  if (type === 2 && situation === 1) {
    return "DIY tax filing works for the simplest situations — but if your life has gotten more complex (new home, investments, side income, marriage, children), you may be missing deductions and credits that more than pay for professional preparation. Whether you're filing as single, married filing jointly, or head of household, the right CPA ensures you're using the optimal strategy for your specific situation.";
  }

  // Individual with higher income
  if (type === 2 && revenue >= 2) {
    return "At your income level, the difference between basic tax filing and strategic tax planning can be significant. From maximizing retirement contributions and investment tax strategies to ensuring you're using the right filing status and claiming every credit available, there are often meaningful savings hiding in plain sight. A proactive CPA doesn't just file — they plan year-round to minimize what you owe.";
  }

  // Self-employed
  if (type === 1) {
    return "Self-employed professionals face unique tax challenges — quarterly estimates, self-employment tax, home office deductions, and business expense tracking. Whether you're a 1099 contractor or running a side business alongside a W-2 job, a proactive CPA can help you structure things properly, choose the right filing approach, and uncover savings you likely didn't know existed.";
  }

  // Real estate investor
  if (type === 3) {
    return "Real estate investors often have complex tax situations with depreciation, 1031 exchanges, passive activity rules, and cost segregation opportunities. Whether you own one rental property or a growing portfolio, the right tax strategy can dramatically improve your after-tax returns. We specialize in helping investors keep more of their rental income and gains.";
  }

  // Retired or approaching retirement
  if (type === 4) {
    return "Retirement brings a new set of tax considerations — Social Security taxation, required minimum distributions, Roth conversion strategies, Medicare surcharge thresholds, and estate planning. The decisions you make in these years can have lasting financial impact. We help retirees and pre-retirees build a tax-efficient withdrawal strategy so your savings last longer and your tax bill stays lower.";
  }

  return "Every financial situation is different — whether you're an individual filing a straightforward return, a family navigating life changes, or a business owner scaling operations. One thing is consistent: most people we meet for the first time are leaving money on the table. A no-pressure conversation with one of our advisors is the fastest way to find out where you stand.";
};

export const Assessment = ({ onOpenChecklist }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);

  const selectAnswer = (optionIdx) => {
    const newAnswers = [...answers];
    newAnswers[step] = optionIdx;
    setAnswers(newAnswers);

    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      setTimeout(() => setDone(true), 200);
    }
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const restart = () => {
    setStep(0);
    setAnswers([]);
    setDone(false);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const getAssessmentData = () => ({
    completed: done,
    visitor_type: answers[0] !== undefined ? questions[0].options[answers[0]] : null,
    revenue_range: answers[1] !== undefined ? questions[1].options[answers[1]] : null,
    current_situation: answers[2] !== undefined ? questions[2].options[answers[2]] : null,
    top_priority: answers[3] !== undefined ? questions[3].options[answers[3]] : null
  });

  // Store assessment data on window for the lead form to pick up
  if (done) {
    window.__kgob_assessment = getAssessmentData();
  }

  return (
    <section
      id="assessment"
      className="relative bg-white py-20 lg:py-28"
      data-testid="welcome-assessment"
    >
      <div className="absolute inset-0 bg-[radial-gradient(600px_300px_at_50%_0%,rgba(31,163,163,0.05),transparent)]" />

      <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            Quick Check: Could You Be Saving More?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4C5A70]">
            Answer 4 quick questions. We'll give you a snapshot of where you might be leaving money on the table.
          </p>
        </motion.div>

        {!done ? (
          <div className="bg-[#F7F6F2] rounded-2xl p-6 sm:p-8 border border-gray-100">
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-xs text-[#4C5A70] mb-2">
                <span>Question {step + 1} of {questions.length}</span>
                <span>{Math.round(((step) / questions.length) * 100)}% complete</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#D4AF37] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((step) / questions.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <h3 className="text-lg sm:text-xl font-semibold text-[#0B2240] mb-6" data-testid="assessment-question">
                  {questions[step].text}
                </h3>

                <div className="space-y-3">
                  {(questions[step].options || []).map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectAnswer(idx)}
                      className={`w-full text-left px-5 py-4 rounded-xl border text-sm sm:text-base transition-all duration-200 ${
                        answers[step] === idx
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B2240] font-medium'
                          : 'border-gray-200 bg-white hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5 text-[#0B2240]'
                      }`}
                      data-testid={`assessment-option-${idx}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                {step > 0 && (
                  <button
                    onClick={goBack}
                    className="mt-4 flex items-center gap-1 text-sm text-[#4C5A70] hover:text-[#0B2240] transition-colors"
                    data-testid="assessment-back"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-[#F7F6F2] rounded-2xl p-6 sm:p-8 border border-gray-100"
            data-testid="assessment-results"
          >
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="h-6 w-6 text-[#1FA3A3]" />
              <h3 className="text-xl font-semibold text-[#0B2240]">
                Your Potential Savings Snapshot
              </h3>
            </div>

            <p className="text-[#4C5A70] leading-relaxed mb-8">
              {getResultText(answers)}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => scrollTo('contact')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-[#0B2240] font-semibold text-base bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_4px_20px_rgba(212,175,55,0.3)] hover:brightness-110 transition-all duration-200"
                data-testid="assessment-cta"
              >
                Let's Talk — Schedule a Free Strategy Session
                <ArrowRight className="h-5 w-5" />
              </button>

              <div className="text-center sm:text-left">
                <button
                  onClick={onOpenChecklist}
                  className="text-sm text-[#1FA3A3] hover:text-[#178585] font-medium transition-colors inline-flex items-center gap-1"
                  data-testid="assessment-checklist-cta"
                >
                  Not ready to talk yet? Explore our interactive Tax Planning Checklist
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <div className="text-center sm:text-left pt-2">
                <button data-testid="assessment-button-1"
                  onClick={restart}
                  className="text-xs text-[#4C5A70] hover:text-[#0B2240] transition-colors"
                >
                  Retake assessment
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
